import withFeatureFlagAuthorization from '@/HOCs/withFeatureFlagAuthorization';
import {
  useCreateFeatureFlag,
  useGetFeatureFlagLogs,
  useGetFeatureFlags,
  useToggleFeatureFlag,
  useUpdateFeatureFlagDescription,
} from '@/services/featureFlags';
import { FeatureFlag, FeatureFlagCreatePayload } from '@/types/featureFlags';
import { capitalizeFirstLetter } from '@/utils/index';
import rem from '@/utils/rem';
import { trackFeatureFlagToggled } from '@/utils/tracking';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ReactNode, useEffect, useMemo, useState } from 'react';

const formatFeatureFlagName = (name: string): string => {
  return name.toLowerCase().replace(/\s+/g, '_');
};

const FeatureFlagAdmin = () => {
  const [newFeatureFlag, setNewFeatureFlag] = useState<FeatureFlagCreatePayload>({
    name: '',
    description: '',
  });
  const [selectedFlag, setSelectedFlag] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const [inputName, setInputName] = useState<string>('');
  const [formattedName, setFormattedName] = useState<string>('');
  const [isViewingLogs, setIsViewingLogs] = useState<boolean>(false);
  const [selectedActionFilter, setSelectedActionFilter] = useState<string>('all');
  const [selectedMonthFilter, setSelectedMonthFilter] = useState<string>('all');

  const toast = useToast();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  const {
    data: featureFlags = [],
    isLoading: isLoadingFlags,
    error: flagsError,
  } = useGetFeatureFlags({
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const {
    data: featureFlagLogs = [],
    isLoading: isLoadingLogs,
    error: logsError,
  } = useGetFeatureFlagLogs({
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const createFeatureFlagMutation = useCreateFeatureFlag();
  const toggleFeatureFlagMutation = useToggleFeatureFlag();
  const updateFeatureFlagDescriptionMutation = useUpdateFeatureFlagDescription();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const drawerInfoBgColor = useColorModeValue('gray.50', 'gray.700');

  const getCurrentEnvironment = () => {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes('dev') || hostname.includes('local')) {
      return 'DEV';
    } else if (hostname.includes('uat')) {
      return 'UAT';
    } else {
      return 'PRODUCTION';
    }
  };

  const currentEnvironment = getCurrentEnvironment();

  const getBannerColor = () => {
    switch (currentEnvironment) {
      case 'PRODUCTION':
        return 'red.600';
      case 'UAT':
        return 'purple.500';
      case 'DEV':
      default:
        return 'blue.500';
    }
  };

  useEffect(() => {
    document.body.style.overflow = 'auto';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const renderDataChanges = (oldData: any, newData: any, action?: string): ReactNode => {
    if (!oldData && !newData) return null;

    const changes: ReactNode[] = [];

    const formatPropertyName = (key: string): string => {
      return capitalizeFirstLetter(key);
    };

    if (oldData && newData) {
      Object.keys({ ...oldData, ...newData }).forEach((key) => {
        if (key === 'modified_at') return;

        if (key === 'updated_by_id') {
          return;
        }

        if (oldData[key] !== newData[key]) {
          const oldValue = String(oldData[key] ?? 'Not set');
          const newValue = String(newData[key] ?? 'Not set');

          if (key === 'description') {
            changes.push(
              <VStack key={key} align="flex-start" spacing={1}>
                <Text fontWeight="bold">{formatPropertyName(key)}:</Text>
                <VStack align="flex-start" paddingLeft={2} fontSize="sm" spacing={0}>
                  <Text borderRadius="md" paddingX={2} paddingY={0.5}>
                    {oldValue}
                  </Text>
                  <Text>↓</Text>
                  <Text fontWeight="bold" borderRadius="md" paddingX={2} paddingY={0.5}>
                    {newValue}
                  </Text>
                </VStack>
              </VStack>,
            );
          } else {
            changes.push(
              <HStack key={key} spacing={1}>
                {key === 'is_enabled' ? (
                  <></>
                ) : (
                  <Text fontWeight={key === 'name' ? 'bold' : 'normal'}>{formatPropertyName(key)}:</Text>
                )}
                <Text borderRadius="md" paddingX={2} paddingY={0.5}>
                  {oldValue === 'false' ? 'Hidden' : oldValue === 'true' ? 'LIVE' : oldValue}
                </Text>
                <Text>→</Text>
                <Text fontWeight="bold" borderRadius="md" paddingX={2} paddingY={0.5}>
                  {newValue === 'false' ? 'Hidden' : newValue === 'true' ? 'LIVE' : newValue}
                </Text>
              </HStack>,
            );
          }
        }
      });
    } else if (!oldData && newData) {
      Object.entries(newData).forEach(([key, value]) => {
        if (key === 'modified_at') return;
        if (action === 'INSERT' && !['name', 'description', 'is_enabled'].includes(key)) return;

        if (key === 'description') {
          if (value === '') return;
          changes.push(
            <VStack key={key} align="flex-start" spacing={1}>
              <Text fontWeight="bold">{formatPropertyName(key)}:</Text>
              <Text paddingLeft={2} fontSize="sm" fontWeight="bold" borderRadius="md" paddingX={2} paddingY={0.5}>
                {String(value)}
              </Text>
            </VStack>,
          );
        } else {
          changes.push(
            <HStack key={key} spacing={1}>
              {key === 'is_enabled' ? (
                <></>
              ) : (
                <Text fontWeight={key === 'name' ? 'bold' : 'normal'}>{formatPropertyName(key)}:</Text>
              )}
              <Text fontWeight="bold" borderRadius="md" paddingX={2} paddingY={0.5}>
                {String(value) === 'false' ? 'Hidden' : String(value) === 'true' ? 'LIVE' : String(value)}
              </Text>
            </HStack>,
          );
        }
      });
    } else if (oldData && !newData) {
      Object.entries(oldData).forEach(([key, value]) => {
        if (key === 'modified_at') return;
        if (action === 'DELETE' && !['name', 'description'].includes(key)) return;

        if (key === 'description') {
          changes.push(
            <VStack key={key} align="flex-start" spacing={1}>
              <Text fontWeight="bold">{formatPropertyName(key)}:</Text>
              <Text paddingLeft={2} fontSize="sm" borderRadius="md" paddingX={2} paddingY={0.5}>
                {String(value)} (Deleted)
              </Text>
            </VStack>,
          );
        } else {
          changes.push(
            <HStack key={key} spacing={1}>
              <Text fontWeight={key === 'name' ? 'bold' : 'normal'}>{formatPropertyName(key)}:</Text>
              <Text borderRadius="md" paddingX={2} paddingY={0.5}>
                {String(value)} (Deleted)
              </Text>
            </HStack>,
          );
        }
      });
    }

    return changes.length > 0 ? <Stack spacing={2}>{changes}</Stack> : null;
  };

  const handleToggleFlag = async (flagId: string, isEnabled: boolean) => {
    try {
      const flagToToggle = featureFlags.find((flag) => flag.id === flagId);

      if (!flagToToggle) {
        throw new Error('Feature flag not found');
      }

      const previousState = flagToToggle.isEnabled;

      await toggleFeatureFlagMutation.mutateAsync({ id: flagId, isEnabled });

      trackFeatureFlagToggled({ ...flagToToggle, isEnabled }, previousState);

      toast({
        title: 'Feature flag updated',
        description: `Feature flag updated, the feature is now ${isEnabled ? 'LIVE' : 'Hidden'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Update failed',
        description: 'Failed to update feature flag',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getDescriptionForFlag = (flagName: string) => {
    const flag = featureFlags.find((f) => f.name === flagName);
    return flag ? flag.description : '';
  };

  const handleOpenEdit = (flagName: string) => {
    setSelectedFlag(flagName);
    setEditDescription(getDescriptionForFlag(flagName));
    onEditOpen();
  };

  const handleUpdateDescription = async () => {
    if (!selectedFlag || !editDescription.trim()) {
      toast({
        title: 'Invalid description',
        description: 'Please enter a description for the feature flag',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const flagToUpdate = featureFlags.find((flag) => flag.name === selectedFlag);

      if (!flagToUpdate) {
        throw new Error('Feature flag not found');
      }

      await updateFeatureFlagDescriptionMutation.mutateAsync({
        id: flagToUpdate.id,
        description: editDescription.trim(),
      });

      onEditClose();

      toast({
        title: 'Description updated',
        description: 'Successfully updated feature flag description',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Update failed',
        description: 'Failed to update feature flag description',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputName(value);
    const formatted = formatFeatureFlagName(value);
    setFormattedName(formatted);
    setNewFeatureFlag({
      ...newFeatureFlag,
      name: formatted,
    });
  };

  const handleCreateFeatureFlag = async () => {
    if (!newFeatureFlag.name.trim()) {
      toast({
        title: 'Invalid name',
        description: 'Please enter a name for the feature flag',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await createFeatureFlagMutation.mutateAsync({
        ...newFeatureFlag,
        name: formattedName || formatFeatureFlagName(newFeatureFlag.name),
      });

      setNewFeatureFlag({
        name: '',
        description: '',
      });
      setInputName('');
      setFormattedName('');

      onCreateClose();

      toast({
        title: 'Feature flag created',
        description: 'Successfully created feature flag',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Failed to create feature flag',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast({
          title: 'Copied to clipboard',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      },
      (err) => {
        console.error('Could not copy text: ', err);
        toast({
          title: 'Failed to copy',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      },
    );
  };

  const renderFlagActions = (flagName: string) => (
    <Menu>
      <MenuButton as={IconButton} aria-label="Options" icon={<span>⋮</span>} size="sm" variant="ghost" />
      <MenuList>
        <MenuItem onClick={() => handleOpenEdit(flagName)}>Edit Description</MenuItem>
      </MenuList>
    </Menu>
  );

  const getAvailableMonths = () => {
    const months = new Set<string>();
    featureFlagLogs.forEach((log) => {
      const date = new Date(log.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.add(monthKey);
    });
    return Array.from(months).sort().reverse();
  };

  const filteredLogs = useMemo(() => {
    return featureFlagLogs.filter((log) => {
      const matchesAction = selectedActionFilter === 'all' || log.action === selectedActionFilter;

      if (selectedMonthFilter === 'all') {
        return matchesAction;
      }

      const logDate = new Date(log.createdAt);
      const logMonthKey = `${logDate.getFullYear()}-${String(logDate.getMonth() + 1).padStart(2, '0')}`;
      return matchesAction && logMonthKey === selectedMonthFilter;
    });
  }, [featureFlagLogs, selectedActionFilter, selectedMonthFilter]);

  const formatMonthOption = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };

  const renderActivityLogs = () => (
    <Card marginBottom={rem(48)} background={bgColor} borderColor={borderColor} boxShadow="md">
      <CardHeader>
        <Flex alignItems="center" justifyContent="space-between">
          <Box>
            <Heading size="md">Activity Logs</Heading>
            <Text marginTop={1} color="gray.500" fontSize="sm">
              Recent feature flag changes
            </Text>
          </Box>
          <Button colorScheme="gray" onClick={() => setIsViewingLogs(false)} size="sm">
            Back to Feature Flags
          </Button>
        </Flex>
        <Stack direction={['column', 'row']} marginTop={4} spacing={4}>
          <FormControl maxWidth="200px">
            <FormLabel fontSize="sm">Filter by Action</FormLabel>
            <Select onChange={(e) => setSelectedActionFilter(e.target.value)} size="sm" value={selectedActionFilter}>
              <option value="all">All Actions</option>
              <option value="INSERT">Created</option>
              <option value="UPDATE">Updated</option>
              <option value="DELETE">Deleted</option>
            </Select>
          </FormControl>
          <FormControl maxWidth="200px">
            <FormLabel fontSize="sm">Filter by Month</FormLabel>
            <Select onChange={(e) => setSelectedMonthFilter(e.target.value)} size="sm" value={selectedMonthFilter}>
              <option value="all">All Time</option>
              {getAvailableMonths().map((monthKey) => (
                <option key={monthKey} value={monthKey}>
                  {formatMonthOption(monthKey)}
                </option>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </CardHeader>
      <CardBody>
        {isLoadingLogs ? (
          <Flex alignItems="center" justifyContent="center" padding={8}>
            <Spinner size="xl" />
          </Flex>
        ) : logsError ? (
          <Text color="red.500" textAlign="center" paddingY={4}>
            Error loading activity logs: {logsError.message}
          </Text>
        ) : filteredLogs.length === 0 ? (
          <Text color="gray.500" textAlign="center" paddingY={4}>
            No activity logs found for the selected filters
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Timestamp</Th>
                  <Th>Flag Name</Th>
                  <Th>Action</Th>
                  <Th>Changed By</Th>
                  <Th>Changes (Old/New)</Th>
                </Tr>
              </Thead>
              <Tbody>
                {[...filteredLogs].map((log) => (
                  <Tr key={log.id}>
                    <Td>{formatDate(log.createdAt)}</Td>
                    <Td fontWeight="medium">{log.newName || log.oldName}</Td>
                    <Td>
                      <Badge colorScheme={log.action === 'INSERT' ? 'green' : log.action === 'DELETE' ? 'red' : 'blue'}>
                        {log.action}
                      </Badge>
                    </Td>
                    <Td>
                      {log.changed_by?.email || 'Unknown user'}
                      {log.changed_by?.firstName && log.changed_by?.lastName && (
                        <Text color="gray.500" fontSize="sm">
                          {log.changed_by.firstName} {log.changed_by.lastName}
                        </Text>
                      )}
                    </Td>
                    <Td>{renderDataChanges(log.oldData, log.newData, log.action)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </CardBody>
    </Card>
  );

  const sortedFeatureFlags = useMemo(() => {
    return [...featureFlags].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [featureFlags]);

  if (isLoadingFlags) {
    return (
      <Flex alignItems="center" justifyContent="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (flagsError) {
    return (
      <Flex alignItems="center" justifyContent="center" height="100vh" padding={4}>
        <Text>Error loading feature flags: {flagsError.message}</Text>
      </Flex>
    );
  }

  return (
    <Box position="relative" overflowY="auto" width="100%" height="100vh">
      <Box maxWidth="1200px" margin="0 auto" padding={rem(32)} paddingTop={rem(48)} paddingBottom={rem(96)}>
        <Button
          marginBottom={4}
          colorScheme="gray"
          leftIcon={<span>←</span>}
          onClick={() => window.history.back()}
          size="sm"
          variant="outline"
        >
          Back to Site
        </Button>

        <Flex alignItems="center" justifyContent="space-between" marginBottom={rem(32)}>
          <Heading as="h1" size="xl">
            Feature Flags Administration
          </Heading>
          <HStack spacing={4}>
            {!isViewingLogs && (
              <Button colorScheme="gray" onClick={() => setIsViewingLogs(true)}>
                View Activity Logs
              </Button>
            )}
            {!isViewingLogs && (
              <Button
                colorScheme="cyan"
                isDisabled={createFeatureFlagMutation.isLoading}
                isLoading={createFeatureFlagMutation.isLoading}
                onClick={onCreateOpen}
              >
                New Feature Flag
              </Button>
            )}
          </HStack>
        </Flex>

        {isViewingLogs ? (
          renderActivityLogs()
        ) : (
          <Card marginBottom={rem(48)} background={bgColor} borderColor={borderColor} boxShadow="md">
            <CardHeader>
              <Flex alignItems="center" justifyContent="space-between">
                <Box>
                  <Heading size="md">Feature Flags</Heading>
                  <Text marginTop={1} color="gray.500" fontSize="sm">
                    Toggle feature flags for current environment
                  </Text>
                </Box>
              </Flex>
            </CardHeader>
            <Box
              color="white"
              fontSize="lg"
              fontWeight="bold"
              textAlign="center"
              background={getBannerColor()}
              paddingY={3}
            >
              Current Environment: {currentEnvironment}
            </Box>
            <CardBody>
              {featureFlags.length === 0 ? (
                <Text color="gray.500" textAlign="center" paddingY={4}>
                  No feature flags found. Create one to get started.
                </Text>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Name</Th>
                        <Th>Description</Th>
                        <Th width="150px">Status</Th>
                        <Th width="150px">Toggle</Th>
                        <Th width="200px">Created At</Th>
                        <Th width="50px"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {sortedFeatureFlags.map((flag: FeatureFlag) => (
                        <Tr key={flag.id}>
                          <Td fontWeight="medium">
                            <Flex alignItems="center">
                              {flag.name}
                              <Tooltip label="Copy flag name">
                                <IconButton
                                  marginLeft={2}
                                  aria-label="Copy flag name"
                                  icon={<span>📋</span>}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyToClipboard(flag.name);
                                  }}
                                  size="xs"
                                  variant="ghost"
                                />
                              </Tooltip>
                            </Flex>
                          </Td>
                          <Td>{flag.description}</Td>
                          <Td>
                            <Badge colorScheme={flag.isEnabled ? 'green' : 'gray'}>
                              {flag.isEnabled ? 'LIVE' : 'Hidden'}
                            </Badge>
                          </Td>
                          <Td>
                            <Switch
                              colorScheme="cyan"
                              isChecked={flag.isEnabled}
                              isDisabled={toggleFeatureFlagMutation.isLoading}
                              onChange={() => handleToggleFlag(flag.id, !flag.isEnabled)}
                            />
                          </Td>
                          <Td>{formatDate(flag.created_at)}</Td>
                          <Td>{renderFlagActions(flag.name)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </CardBody>
          </Card>
        )}

        <Drawer isOpen={isCreateOpen} onClose={onCreateClose} placement="right" size="md">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">Create New Feature Flag</DrawerHeader>

            <DrawerBody>
              <Stack marginTop={4} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Feature Flag Name</FormLabel>
                  <Input onChange={handleNameChange} placeholder="e.g. My New Feature" value={inputName} />
                  {inputName && (
                    <Text marginTop={2} color="green.500" fontSize="sm">
                      Will be saved as: <code>{formattedName}</code>
                    </Text>
                  )}
                  <Text marginTop={1} color="white.500" fontSize="sm">
                    Names will be converted to lowercase with underscores (e.g. "My Feature" → "my_feature")
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    onChange={(e) => setNewFeatureFlag({ ...newFeatureFlag, description: e.target.value })}
                    placeholder="Describe what this feature flag controls"
                    value={newFeatureFlag.description}
                  />
                </FormControl>

                <Box padding={4} background={drawerInfoBgColor} borderRadius="md">
                  <Text marginBottom={2} fontWeight="medium">
                    What happens next?
                  </Text>
                  <Text fontSize="sm">
                    This feature flag will be created in the current environment and will be disabled by default.
                  </Text>
                </Box>
              </Stack>
            </DrawerBody>

            <DrawerFooter borderTopWidth="1px">
              <Button marginRight={3} onClick={onCreateClose} variant="outline">
                Cancel
              </Button>
              <Button
                colorScheme="cyan"
                isDisabled={!inputName.trim() || createFeatureFlagMutation.isLoading}
                isLoading={createFeatureFlagMutation.isLoading}
                onClick={handleCreateFeatureFlag}
              >
                Create
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        <Drawer isOpen={isEditOpen} onClose={onEditClose} placement="right" size="md">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">Edit Description: {selectedFlag}</DrawerHeader>

            <DrawerBody>
              <Stack marginTop={4} spacing={4}>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Describe what this feature flag controls"
                    value={editDescription}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>

            <DrawerFooter borderTopWidth="1px">
              <Button marginRight={3} onClick={onEditClose} variant="outline">
                Cancel
              </Button>
              <Button
                colorScheme="cyan"
                isDisabled={!editDescription.trim() || updateFeatureFlagDescriptionMutation.isLoading}
                isLoading={updateFeatureFlagDescriptionMutation.isLoading}
                onClick={handleUpdateDescription}
              >
                Update
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Box>
    </Box>
  );
};

export default withFeatureFlagAuthorization(FeatureFlagAdmin);
